import { SupportFormData, FormErrors, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../types/form';

export const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return 'Email address is required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return undefined;
};

export const validateName = (name: string): string | undefined => {
  if (!name.trim()) {
    return undefined; // Name is now optional
  }
  
  if (name.trim().length < 2) {
    return 'Name must be at least 2 characters';
  }
  
  // Allow any Unicode letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[\p{L}\p{M}\s'\-\.]+$/u;
  if (!nameRegex.test(name)) {
    return 'Name contains invalid characters';
  }
  
  return undefined;
};


export const validatePaymentDate = (paymentDate: string): string | undefined => {
  if (!paymentDate.trim()) {
    return undefined; // Payment date is now optional
  }
  
  const date = new Date(paymentDate);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  
  if (isNaN(date.getTime())) {
    return 'Please select a valid date';
  }
  
  if (date > today) {
    return 'Payment date cannot be in the future';
  }
  
  return undefined;
};

export const validateTransactionAmount = (amount: string): string | undefined => {
  if (!amount.trim()) {
    return undefined; // Transaction amount is now optional
  }
  
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount) || numericAmount < 0) {
    return 'Please enter a valid amount (0 or greater)';
  }
  
  return undefined;
};


export const validateMessage = (message: string): string | undefined => {
  if (!message.trim()) {
    return undefined; // Message is now optional
  }
  
  if (message.trim().length < 10) {
    return 'Message must be at least 10 characters';
  }
  
  if (message.length > 1800) {
    return 'Message cannot exceed 1800 characters';
  }
  
  return undefined;
};

export const validateFile = (file: File | null): string | undefined => {
  if (!file) {
    return undefined; // File is optional
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return 'File size cannot exceed 5MB';
  }
  
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return 'Only images (JPG, PNG), PDFs, and Word documents are allowed';
  }
  
  return undefined;
};

export const validateForm = (formData: SupportFormData): FormErrors => {
  const errors: FormErrors = {};
  
  // Only validate fields that have content (except email which is always required)
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  // Only validate other fields if they have content
  if (formData.name.trim()) {
    const nameError = validateName(formData.name);
    if (nameError) errors.name = nameError;
  }
  
  if (formData.paymentDate.trim()) {
    const paymentDateError = validatePaymentDate(formData.paymentDate);
    if (paymentDateError) errors.paymentDate = paymentDateError;
  }
  
  if (formData.transactionAmount.trim()) {
    const transactionAmountError = validateTransactionAmount(formData.transactionAmount);
    if (transactionAmountError) errors.transactionAmount = transactionAmountError;
  }
  
  if (formData.message.trim()) {
    const messageError = validateMessage(formData.message);
    if (messageError) errors.message = messageError;
  }
  
  const fileError = validateFile(formData.file || null);
  if (fileError) errors.file = fileError;
  
  return errors;
};

export const isFormValid = (errors: FormErrors): boolean => {
  return Object.keys(errors).length === 0;
};
